/**
 * Shimmer loading skeletons — drop-in replacements for Spinner loading states.
 * Usage: <Skeleton.Table rows={5} cols={4} /> or compose your own with <Skeleton.Line />
 */

function Line({ className = '', width = 'w-full', height = 'h-3' }) {
  return (
    <div className={`animate-pulse rounded-md bg-gray-200/70 ${width} ${height} ${className}`} />
  );
}

function Circle({ size = 'h-10 w-10', className = '' }) {
  return <div className={`animate-pulse rounded-full bg-gray-200/70 ${size} ${className}`} />;
}

function Card({ className = '' }) {
  return (
    <div className={`rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-900/[0.04] ${className}`}>
      <Line width="w-20" height="h-2" className="mb-3" />
      <Line width="w-32" height="h-6" className="mb-1" />
      <Line width="w-16" height="h-2" />
    </div>
  );
}

function KPIRow({ count = 4 }) {
  return (
    <div className={`grid gap-4 grid-cols-${count}`}>
      {Array.from({ length: count }).map((_, i) => <Card key={i} />)}
    </div>
  );
}

function TableRow({ cols = 5 }) {
  return (
    <tr className="border-b border-gray-50">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Line width={i === 0 ? 'w-28' : 'w-16'} height="h-3" />
        </td>
      ))}
    </tr>
  );
}

function Table({ rows = 5, cols = 5 }) {
  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/[0.04]">
      <table className="w-full">
        <thead className="border-b border-gray-100 bg-gray-50/50">
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <Line width="w-14" height="h-2" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => <TableRow key={i} cols={cols} />)}
        </tbody>
      </table>
    </div>
  );
}

function PatientList({ rows = 8 }) {
  return (
    <div className="space-y-5">
      {/* Toolbar skeleton */}
      <div className="flex items-center gap-3">
        <Line width="w-72" height="h-10" className="rounded-xl" />
        <Line width="w-36" height="h-10" className="rounded-xl" />
        <div className="flex-1" />
        <Line width="w-28" height="h-9" className="rounded-md" />
      </div>
      <Table rows={rows} cols={6} />
    </div>
  );
}

function PatientDetail() {
  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/[0.04]">
        <div className="flex items-start gap-4">
          <Circle size="h-14 w-14" className="rounded-2xl" />
          <div className="flex-1 space-y-2">
            <Line width="w-48" height="h-5" />
            <div className="flex gap-4">
              <Line width="w-24" height="h-3" />
              <Line width="w-20" height="h-3" />
              <Line width="w-16" height="h-3" />
            </div>
            <div className="flex gap-3">
              <Line width="w-28" height="h-3" />
              <Line width="w-36" height="h-3" />
            </div>
          </div>
        </div>
      </div>
      {/* Quick stats */}
      <KPIRow count={4} />
      {/* Tabs placeholder */}
      <div className="flex gap-6 border-b border-gray-200 pb-3">
        {['w-16', 'w-24', 'w-20', 'w-16', 'w-14'].map((w, i) => (
          <Line key={i} width={w} height="h-3" />
        ))}
      </div>
      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-900/[0.04] space-y-3">
          <Line width="w-32" height="h-4" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <Line width="w-20" height="h-3" />
              <Line width="w-28" height="h-3" />
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-900/[0.04] space-y-3">
          <Line width="w-28" height="h-4" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <Line width="w-24" height="h-3" />
              <Line width="w-20" height="h-3" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Schedule() {
  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <Line width="w-24" height="h-9" className="rounded-lg" />
        <Line width="w-48" height="h-9" className="rounded-lg" />
        <div className="flex-1" />
        <Line width="w-20" height="h-9" className="rounded-lg" />
        <Line width="w-20" height="h-9" className="rounded-lg" />
      </div>
      {/* Calendar grid */}
      <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-900/[0.04] p-4">
        <div className="grid grid-cols-7 gap-2 mb-3">
          {Array.from({ length: 7 }).map((_, i) => <Line key={i} height="h-3" width="w-full" />)}
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 py-2 border-b border-gray-50">
            <Line width="w-12" height="h-3" />
            <div className="flex-1 grid grid-cols-3 gap-2">
              {i % 2 === 0 && <Line height="h-8" className="rounded-md bg-teal-100/50" />}
              {i % 3 === 0 && <Line height="h-8" className="rounded-md bg-indigo-100/50" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Line width="w-56" height="h-6" />
          <Line width="w-32" height="h-3" />
        </div>
        <div className="flex gap-2">
          <Line width="w-28" height="h-8" className="rounded-lg" />
          <Line width="w-24" height="h-8" className="rounded-lg" />
        </div>
      </div>
      <KPIRow count={4} />
      <div className="grid gap-6 lg:grid-cols-2">
        <ChartPlaceholder />
        <ChartPlaceholder />
      </div>
    </div>
  );
}

function ChartPlaceholder() {
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-900/[0.04]">
      <Line width="w-32" height="h-4" className="mb-4" />
      <div className="flex items-end gap-1 h-[180px]">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="flex-1 animate-pulse rounded-t bg-gray-100" style={{ height: `${30 + Math.random() * 70}%` }} />
        ))}
      </div>
    </div>
  );
}

function Billing() {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Line key={i} width="w-20" height="h-8" className="rounded-lg" />
        ))}
        <div className="flex-1" />
        <Line width="w-28" height="h-9" className="rounded-md" />
      </div>
      <KPIRow count={4} />
      <Table rows={6} cols={7} />
    </div>
  );
}

function Report() {
  return (
    <div className="space-y-6">
      <KPIRow count={4} />
      <div className="grid gap-6 lg:grid-cols-2">
        <ChartPlaceholder />
        <ChartPlaceholder />
      </div>
      <Table rows={5} cols={5} />
    </div>
  );
}

function Lines({ count = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <Line key={i} width={i === count - 1 ? 'w-2/3' : 'w-full'} />
      ))}
    </div>
  );
}

const Skeleton = {
  Line,
  Lines,
  Circle,
  Card,
  KPIRow,
  Table,
  TableRow,
  PatientList,
  PatientDetail,
  Schedule,
  Dashboard,
  Billing,
  Report,
  ChartPlaceholder,
};

export default Skeleton;
